import axios from 'axios';
import { message } from 'antd';
import { setupCache } from 'axios-cache-interceptor';
import { oidcUserToSession, refreshOidcSession } from './auth/oidc';

// Function to get the subdomain (tenant)
function getSubdomain() {
  const hostname = window.location.hostname; // e.g., secure1.seeward.io
  // Extract subdomain, e.g., "secure1"
  return hostname.split('.')[0];
}

/**
 * The appliance's licence gate answers 402 `LICENCE_EXPIRED` to every call once a trial or
 * licence has ended, sign-in and token refresh included. Nothing can be done with a session
 * after that, so it is cleared the way an expired one is. The destination is the trial-ended
 * screen, though, not sign-in, which would only earn the same 402.
 */
export const isLicenceRefusal = (error: any): boolean =>
  error?.response?.status === 402 && error?.response?.data?.code === 'LICENCE_EXPIRED';

/**
 * Set once the page is on its way to `/licence`. A refused refresh reaches both this and the
 * refresh path's own `redirectLogin`, and the later of two `location.href` assignments wins.
 */
let licenceRedirecting = false;

function redirectLicence() {
  licenceRedirecting = true;
  if (tokenRefreshTimeout) clearTimeout(tokenRefreshTimeout);
  window.sessionStorage.removeItem('auth');
  window.localStorage.removeItem('isLogout');
  message.destroy();
  if (!window.location.pathname.startsWith('/licence')) {
    window.location.href = '/licence';
  }
}

// Raw `axios` carries sign-in, recovery, the second factor and token refresh, and the gate
// covers all of them. Instances from `api()` do not inherit this, so theirs is below.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isLicenceRefusal(error)) redirectLicence();
    return Promise.reject(error);
  }
);

// Function to handle login redirection
function redirectLogin() {
  if (licenceRedirecting) return;
  console.log('Redirecting to login due to session expiration...');
  if (tokenRefreshTimeout) clearTimeout(tokenRefreshTimeout);
  window.sessionStorage.removeItem('auth');
  window.localStorage.removeItem('isLogout');

  const currentPath = window.location.pathname;
  window.localStorage.setItem('lastVisitedPath', currentPath);
  if (!window.location.href.includes('/login')) {
    window.location.href = '/login';
  }
  message.destroy();
}

// Function to handle the refresh token process
let refreshInFlight: Promise<string> | null = null;

/**
 * Renew through Keycloak only. Multiple API calls can receive a 401 together;
 * they must share one renewal instead of invalidating each other's refresh token.
 * Throws when the renewal fails: what a failure means is up to the caller.
 */
function renewTokens(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const user = await refreshOidcSession();
      const stored = JSON.parse(sessionStorage.getItem('auth') || '{}');
      sessionStorage.setItem('auth', JSON.stringify({
        ...stored,
        current: { ...stored.current, ...oidcUserToSession(user) }
      }));
      return user.access_token;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** The 401 path: a session that cannot be renewed is over, so the person goes to sign in. */
function refreshAccessToken(): Promise<string> {
  return renewTokens().catch((error) => {
    console.error('Keycloak session renewal failed:', error);
    redirectLogin();
    throw error;
  });
}

/**
 * Renew the session on demand, for the moment something has just changed what the signed-in
 * person is. Accepting a support admin invitation grants a Keycloak role, and the server
 * resolves roles per access token, so the token the browser already holds can keep answering
 * with the old ones; a renewed token is looked up afresh.
 *
 * It shares the one in-flight renewal with the 401 path, and re-arms the scheduled refresh
 * for the new token. A failed renewal is reported rather than acted on: unlike the 401 path
 * it does not send the person to sign in, because the caller has something better to say.
 */
export async function renewSession(): Promise<boolean> {
  try {
    await renewTokens();
    setupTokenRefresh();
    return true;
  } catch (error) {
    console.error('Keycloak session renewal failed:', error);
    return false;
  }
}

// Create and configure axios instance
export const api = () => {
  const user = sessionStorage.getItem('auth');
  const tmpAuth = user ? JSON.parse(user).current : {};
  // Get the subdomain to identify the tenant
  const tenantSubdomain = getSubdomain();

  const instance = axios.create({
    headers: {
      'Authorization': `Bearer ${tmpAuth.access_token}`,
      'X-Tenant-ID': tenantSubdomain,
      'x-workspace-id': tmpAuth.default_workspace?.id
    }
  });

  // Setup axios cache interceptor
  const cache = setupCache(instance, {});

  // Interceptor to handle token refresh
  cache.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest = error.config;
      if (isLicenceRefusal(error)) {
        redirectLicence();
        return Promise.reject(error);
      }
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newAccessToken = await refreshAccessToken();
          originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
          originalRequest.headers['X-Tenant-ID'] = tenantSubdomain; // Ensure tenant header is retained
          return cache(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          redirectLogin();
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

let tokenRefreshTimeout: NodeJS.Timeout | null = null;

/**
 * Stop the pending refresh. Signing out calls this.
 *
 * Without it the timer scheduled for the old session survives the sign-out and fires minutes
 * later into an empty storage, where `refreshAccessToken` raises a "Session Expired" popup at
 * someone who left on purpose.
 */
export function cancelTokenRefresh() {
  if (tokenRefreshTimeout) {
    clearTimeout(tokenRefreshTimeout);
    tokenRefreshTimeout = null;
  }
}

// Function to parse JWT and get its expiry time
function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const decodedPayload = JSON.parse(atob(base64));
  return decodedPayload.exp * 1000; // Convert expiry to milliseconds
}

export function setupTokenRefresh() {
  cancelTokenRefresh();
  const user = sessionStorage.getItem('auth');
  const tmpAuth = user ? JSON.parse(user).current : null;

  if (tmpAuth && tmpAuth.access_token) {
    const tokenExpiry = parseJwt(tmpAuth.access_token);
    const refreshTime = tokenExpiry - Date.now() - 60 * 1000; // Refresh 1 min before expiry

    if (refreshTime > 0) {
      tokenRefreshTimeout = setTimeout(async () => {
        try {
          await refreshAccessToken();
          setupTokenRefresh(); // Schedule the next refresh
        } catch (err) {
          console.error('Token refresh failed:', err);
          redirectLogin();
        }
      }, refreshTime);
    } else {
      void refreshAccessToken().then(setupTokenRefresh).catch(() => undefined);
    }
  }
}

// Call `setupTokenRefresh` after login or app initialization
function initializeAuth() {
  const user = sessionStorage.getItem('auth');
  if (user) {
    setupTokenRefresh();
  }
}
initializeAuth();
